import type { LensCardData, LensFilterValues } from '~/types/leLens';

export const CS_CONVERSATIONS_PAGE_ID = 'cs-conversations';

export type ReportView = 'conversations' | 'orders' | 'excluded' | 'conversion-rate';
export const REPORT_VIEWS: ReadonlyArray<{ value: ReportView; label: string }> = [
  { value: 'conversations', label: 'CS Conversations and Sales' },
  { value: 'orders', label: 'CS Orders and Conversion Score' },
  { value: 'excluded', label: 'CS Excluded Conversations' },
  { value: 'conversion-rate', label: 'CS Conversion Rate Report' },
];

/** Each dashboard's own filters, declared in svc-reporting's native-specs/cs-conversations.json. */
export const FILTER_DEFINITIONS = [
  { key: 'c_agent', label: 'Final agent', view: 'conversations' },
  { key: 'c_customer', label: 'Customer', view: 'conversations' },
  { key: 'c_type', label: 'Conversion interaction type', view: 'conversations' },
  { key: 'c_score', label: 'Interaction Score Converted', view: 'conversations' },
  { key: 'o_agent', label: 'Agent', view: 'orders' },
  { key: 'o_customer', label: 'Customer', view: 'orders' },
  { key: 'o_conversions', label: 'Conversions', view: 'orders' },
  { key: 'x_agent', label: 'Final agent', view: 'excluded' },
  { key: 'x_customer', label: 'Customer', view: 'excluded' },
  { key: 'x_type', label: 'Conversion interaction type', view: 'excluded' },
  { key: 'r_team_leader', label: 'Team Leader', view: 'conversion-rate' },
  { key: 'r_agent', label: 'Final agent', view: 'conversion-rate' },
] as const;
type FilterKey = (typeof FILTER_DEFINITIONS)[number]['key'];
/** Customer lists run to thousands; the server lists 500, so these also accept a typed customer ID. */
export const FREE_TEXT_FILTERS: ReadonlyArray<FilterKey> = ['c_customer', 'o_customer', 'x_customer'];

type ColumnFormat = 'text' | 'number' | 'currency' | 'percent';
type ReportColumn = { field: string; label: string; format: ColumnFormat; width?: number };

const text = (field: string, label: string, width = 150): ReportColumn => ({ field, label, format: 'text', width });
const count = (field: string, label: string): ReportColumn => ({ field, label, format: 'number' });
const aud = (field: string, label: string): ReportColumn => ({ field, label, format: 'currency' });
const pct = (field: string, label: string): ReportColumn => ({ field, label, format: 'percent' });

/** Columns follow the workbook crosstabs' order. */
const CONVERSATION_COLUMNS: ReadonlyArray<ReportColumn> = [
  text('conversation_id', 'Conversation ID', 290),
  text('final_agent', 'Final agent'),
  text('customer_bk', 'Customer', 290),
  text('conversation_start', 'Conversation start (adjusted)', 170),
  text('conversion_interaction_type', 'Conversion interaction type', 170),
  text('final_queue', 'Final queue'),
  count('order_of_interactions', 'Order of interactions'),
  text('initial_queue', 'Initial queue'),
  text('initial_agent', 'Initial agent'),
  count('final_talk_time', 'Final talk time'),
  text('final_wrap_up_text', 'Final wrap-up text', 220),
  text('zendesk_ticket_ids', 'Zendesk ticket IDs'),
  text('order_bks', 'Order IDs', 220),
  text('contains_cancelled_item', 'Contains cancelled item', 120),
  text('last_refund_date', 'Last refund date', 120),
  count('interaction_score_converted', 'Interaction Score Converted'),
  aud('total_gross_sales_aud', 'Total gross sales AUD'),
  aud('margin_adjusted_aud', 'Margin adjusted AUD'),
  aud('total_margin_aud', 'Total margin AUD'),
  aud('lp_margin_aud', 'LP margin AUD'),
  aud('total_gross_sales_cancelled', 'Total gross sales cancelled AUD'),
  pct('cancelled_share', '% Cancelled (Sales)'),
  count('cnt_of_conversions', 'Cnt of conversions'),
  count('conversions_final', 'Conversions Final'),
  count('cnt_of_orders', 'Cnt of orders'),
  count('cnt_of_items', 'Cnt of items'),
  count('cnt_of_categories', 'Cnt of categories'),
  count('cnt_of_products', 'Cnt of products'),
  count('hotel_item_count', 'Hotel item count'),
  count('hotel_conversion', 'Hotel Conversion'),
  count('tour_item_count', 'Tour item count'),
  count('tour_conversion', 'Tour Conversion'),
  count('cruise_item_count', 'Cruise item count'),
  count('cruise_conversion', 'Cruise Conversion'),
  count('car_hire_item_count', 'Car hire item count'),
  count('unique_car_hire_item_count', 'Unique car hire item count'),
  count('experience_item_count', 'Experience item count'),
  count('experience_conversion', 'Experience Conversion'),
  count('flight_item_count', 'Flight item count'),
  count('flight_conversion', 'Flights Conversion'),
  count('insurance_item_count', 'Insurance item count'),
  count('insurance_conversion', 'Insurance Conversion'),
];

const ORDER_COLUMNS: ReadonlyArray<ReportColumn> = [
  text('order_bk', 'Order ID', 290),
  text('utm_medium', 'UTM medium'),
  text('agent_name', 'Agent'),
  text('customer_bk', 'Customer', 290),
  text('order_date_sydt', 'Order date', 110),
  text('transaction_date', 'Transaction date', 120),
  count('running_count_transactions', 'Running count of transactions'),
  count('days_since_previous_purchase_all', 'Days since previous purchase'),
  text('categories', 'Categories', 200),
  text('contains_cancelled_item', 'Contains cancelled item', 120),
  text('max_refund_date', 'Max refund date', 120),
  count('conversions', 'Conversions'),
  count('experience_conversions', 'Experience conversions'),
  count('lp_attachment_numerator', 'LP Attachment Rate Numerator'),
  count('lp_attachment_denominator', 'LP Attachment Rate Denominator'),
  aud('total_gross_sales_aud', 'Total gross sales AUD'),
  aud('margin_adjusted_aud', 'Margin adjusted AUD'),
  aud('total_margin_aud', 'Total margin AUD'),
  aud('lp_margin_aud', 'LP margin AUD'),
  aud('total_gross_sales_cancelled', 'Total gross sales cancelled AUD'),
  pct('cancelled_share', '% Cancelled (Gross Sales)'),
  count('cnt_of_categories', 'Cnt of categories'),
  count('cnt_of_products', 'Cnt of products'),
  count('cnt_of_items', 'Cnt of items'),
  count('hotel_item_count', 'Hotel item count'),
  count('unique_hotel_item_count', 'Unique hotel item count'),
  count('tour_item_count', 'Tour item count'),
  count('unique_tour_item_count', 'Unique tour item count'),
  count('cruise_item_count', 'Cruise item count'),
  count('unique_cruise_item_count', 'Unique cruise item count'),
  count('custom_offer_item_count', 'Custom offer item count'),
  count('unique_customer_offer_item_count', 'Unique custom offer item count'),
  count('car_hire_item_count', 'Car hire item count'),
  count('unique_car_hire_item_count', 'Unique car hire item count'),
  count('experience_item_count', 'Experience item count'),
  count('unique_experience_item_count', 'Unique experience item count'),
  count('flight_item_count', 'Flight item count'),
  count('unique_flight_item_count', 'Unique flight item count'),
  count('insurance_item_count', 'Insurance item count'),
];

const EXCLUDED_COLUMNS: ReadonlyArray<ReportColumn> = [
  text('conversation_id', 'Conversation ID', 290),
  text('final_agent', 'Final agent'),
  text('customer_bk', 'Customer', 290),
  text('conversation_start', 'P Conversation Start Time Adjusted', 170),
  text('conversion_interaction_type', 'Conversion interaction type', 170),
  text('final_queue', 'Final queue'),
  text('initial_queue', 'Initial queue'),
  text('initial_agent', 'Initial agent'),
  count('final_talk_time', 'Final talk time'),
  text('final_wrap_up_text', 'Final wrap-up text', 220),
  text('zendesk_ticket_ids', 'Zendesk ticket IDs'),
  text('known_vendor_flag', 'Known vendor flag', 120),
];

const RATE_COLUMNS: ReadonlyArray<ReportColumn> = [
  text('final_agent', 'Final agent', 190),
  text('team_leader_name', 'Team Leader', 170),
  count('conversions', 'Conversions'),
  count('interaction_score_adjusted', 'Interaction score adjusted'),
  pct('sales_conversion_rate', 'Sales Conversion Rate'),
  aud('total_gross_sales_aud', 'Total gross sales AUD'),
  aud('margin_adjusted_aud', 'Margin adjusted AUD'),
  count('cnt_of_products', 'Cnt of products'),
  count('unique_customers', 'Unique customers'),
  { field: 'products_per_customer', label: 'Products per Customer', format: 'number' },
  count('total_interactions', 'Total Interactions'),
  count('count_of_excluded_interactions', 'Count of excluded interactions'),
  pct('excluded_share', 'Excluded Interactions %'),
];

type ViewCards = { rows: string; totals: ReadonlyArray<string>; count?: string; columns: ReadonlyArray<ReportColumn> };
export const VIEW_CARDS: Record<ReportView, ViewCards> = {
  conversations: {
    rows: 'conv_rows',
    totals: ['conv_totals_1', 'conv_totals_2', 'conv_totals_3'],
    count: 'conv_count',
    columns: CONVERSATION_COLUMNS,
  },
  orders: {
    rows: 'order_rows',
    totals: ['order_totals_1', 'order_totals_2', 'order_totals_3'],
    count: 'order_count',
    columns: ORDER_COLUMNS,
  },
  excluded: { rows: 'excl_rows', totals: [], count: 'excl_count', columns: EXCLUDED_COLUMNS },
  'conversion-rate': { rows: 'rate_by_agent', totals: ['rate_totals'], columns: RATE_COLUMNS },
};

// The workbook opens on Date (Max) -7 to Date (Max): the eight Sydney days to yesterday.
const DEFAULT_WINDOW_DAYS = 8;

export type PageFilters = {
  reportView: ReportView;
  from: string;
  to: string;
  selections: Record<FilterKey, Array<string>>;
};
export type Row = Record<string, string | number | null>;
type CardResult = { rows: Array<Record<string, unknown>>; truncated: boolean; card: LensCardData };
type CardProblem = { problem: string };

const sydneyDay = (date: Date) => new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Sydney' }).format(date);
const addDays = (day: string, amount: number) =>
  new Date(Date.parse(`${day}T00:00:00Z`) + amount * 86400000).toISOString().slice(0, 10);
const lastCompleteDay = (now = new Date()) => addDays(sydneyDay(now), -1);

export function defaultFilters(reportView: ReportView = 'conversations', now = new Date()): PageFilters {
  const to = lastCompleteDay(now);
  const selections = Object.fromEntries(FILTER_DEFINITIONS.map(({ key }) => [key, [] as Array<string>])) as Record<
    FilterKey,
    Array<string>
  >;
  return { reportView, from: addDays(to, 1 - DEFAULT_WINDOW_DAYS), to, selections };
}

const isDay = (value: string | null): value is string =>
  !!value && /^\d{4}-\d{2}-\d{2}$/.test(value) && new Date(`${value}T00:00:00Z`).toISOString().startsWith(value);

export function filtersError(filters: PageFilters, now = new Date()): string | null {
  if (!isDay(filters.from) || !isDay(filters.to)) {
    return 'Choose valid dates in YYYY-MM-DD format.';
  }
  if (filters.from > filters.to) {
    return 'The start date must be on or before the end date.';
  }
  if (filters.to > lastCompleteDay(now)) {
    return `The CS models are complete to ${lastCompleteDay(now)}. Choose an earlier end date.`;
  }
  return null;
}

export function parseFilters(params: URLSearchParams, now = new Date()): PageFilters {
  const requested = params.get('reportView');
  const reportView = REPORT_VIEWS.some((view) => view.value === requested) ? (requested as ReportView) : undefined;
  const filters = defaultFilters(reportView, now);
  if (isDay(params.get('from'))) {
    filters.from = params.get('from');
  }
  if (isDay(params.get('to'))) {
    filters.to = params.get('to');
  }
  for (const { key } of FILTER_DEFINITIONS) {
    if (!params.has(key)) {
      continue;
    }
    try {
      const value: unknown = JSON.parse(params.get(key));
      if (Array.isArray(value) && value.every((item) => typeof item === 'string')) {
        filters.selections[key] = value;
      }
    } catch {
      // A malformed selection keeps the default.
    }
  }
  return filters;
}

export function writeFilters(filters: PageFilters, current: URLSearchParams, now = new Date()): URLSearchParams {
  const params = new URLSearchParams(current);
  const defaults = defaultFilters(filters.reportView, now);
  params.set('reportView', filters.reportView);
  for (const key of ['from', 'to'] as const) {
    if (filters[key] === defaults[key]) {
      params.delete(key);
    } else {
      params.set(key, filters[key]);
    }
  }
  for (const { key } of FILTER_DEFINITIONS) {
    const values = [...new Set(filters.selections[key])].sort();
    if (values.length) {
      params.set(key, JSON.stringify(values));
    } else {
      params.delete(key);
    }
  }
  return params;
}

/** Every tab's selections are sent; svc binds each filter only to its own tab's cards. */
export function lensFilters(filters: PageFilters): LensFilterValues {
  const values: LensFilterValues = {
    native_view: filters.reportView,
    period: { window: { from: filters.from, to: filters.to }, comparison: null },
  };
  for (const { key } of FILTER_DEFINITIONS) {
    if (filters.selections[key].length) {
      values[key] = filters.selections[key];
    }
  }
  return values;
}

const num = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(typeof value === 'object' && value && 'value' in value ? value.value : value);
  return Number.isFinite(parsed) ? parsed : null;
};
/** BigQuery DATE and NUMERIC values can arrive wrapped as { value }. */
const unwrap = (value: unknown): unknown =>
  typeof value === 'object' && value !== null && 'value' in value ? value.value : value;
const cellText = (value: unknown): string | null => {
  const inner = unwrap(value);
  if (inner === null || inner === undefined) {
    return null;
  }
  return typeof inner === 'object' ? JSON.stringify(inner) : String(inner);
};
const divide = (a: number | null, b: number | null) => (a === null || b === null || b === 0 ? null : a / b);

export function readCard(cards: ReadonlyArray<LensCardData>, cardId: string): CardResult | CardProblem {
  const card = cards.find((item) => item.cardId === cardId);
  if (!card) {
    return { problem: 'This section is still loading.' };
  }
  if (card.status === 'ready' || card.status === 'empty') {
    return { rows: card.rows ?? [], truncated: Boolean(card.truncated), card };
  }
  if (card.status === 'restricted') {
    return { problem: card.message ?? 'You do not have access to this section.' };
  }
  return { problem: `This section could not load. ${card.message ?? ''}`.trim() };
}

export const isProblem = (result: CardResult | CardProblem): result is CardProblem => 'problem' in result;

/**
 * Ratios are worked out from sums, as the workbook's SUM(a) / SUM(b) calcs do, so a total row's rate is the
 * rate of its totals rather than an average of row rates.
 */
export function withRatios(row: Row, view: ReportView): Row {
  if (view === 'conversations' || view === 'orders') {
    return { ...row, cancelled_share: divide(num(row.total_gross_sales_cancelled), num(row.total_gross_sales_aud)) };
  }
  if (view === 'conversion-rate') {
    return {
      ...row,
      sales_conversion_rate: divide(num(row.conversions), num(row.interaction_score_adjusted)),
      products_per_customer: divide(num(row.cnt_of_products), num(row.unique_customers)),
      excluded_share: divide(num(row.count_of_excluded_interactions), num(row.total_interactions)),
    };
  }
  return row;
}

export function tableRows(result: CardResult, view: ReportView): Array<Row> {
  const columns = VIEW_CARDS[view].columns;
  return result.rows.map((raw) =>
    withRatios(
      Object.fromEntries(
        columns.map(({ field, format }) => [field, format === 'text' ? cellText(raw[field]) : num(raw[field])]),
      ),
      view,
    ),
  );
}

/**
 * The view's grand totals from its server-side totals cards, which cover the whole window even when the
 * detail table reaches the row cap. Null while any totals card is missing.
 */
export function totalsOf(cards: ReadonlyArray<LensCardData>, view: ReportView): Row | null {
  const ids = [...VIEW_CARDS[view].totals, ...(VIEW_CARDS[view].count ? [VIEW_CARDS[view].count] : [])];
  const merged: Row = {};
  for (const id of ids) {
    const result = readCard(cards, id);
    if (isProblem(result)) {
      return null;
    }
    for (const [key, value] of Object.entries(result.rows[0] ?? {})) {
      merged[key] = num(value);
    }
  }
  return withRatios(merged, view);
}

export function periodLabel(card: LensCardData | undefined, fallback = ''): string {
  return card?.period?.label?.current ?? fallback;
}

const formatters: Record<Exclude<ColumnFormat, 'text'>, Intl.NumberFormat> = {
  number: new Intl.NumberFormat('en-AU', { maximumFractionDigits: 2 }),
  currency: new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }),
  percent: new Intl.NumberFormat('en-AU', { style: 'percent', maximumFractionDigits: 1 }),
};

export function formatCell(value: string | number | null | undefined, format: ColumnFormat): string {
  if (value === null || value === undefined) {
    return '';
  }
  return format === 'text' || typeof value === 'string' ? String(value) : formatters[format].format(value);
}

export function csvOf(
  title: string,
  range: string,
  columns: ReadonlyArray<ReportColumn>,
  rows: ReadonlyArray<Row>,
  truncated: boolean,
): string {
  const cell = (value: string | number | null | undefined) => {
    const raw = value === null || value === undefined ? '' : String(value);
    // A leading =, +, - or @ would run as a spreadsheet formula; numbers stay numeric.
    const safe = typeof value === 'string' && /^[=+\-@]/.test(raw) ? `'${raw}` : raw;
    return `"${safe.replace(/"/g, '""')}"`;
  };
  const lines: Array<Array<string | number | null>> = [
    ['Report', `CS Conversations & Sales · ${title}`],
    ['Period', range],
    ...(truncated ? [['Note', 'Row limit reached: this file holds the first 10,000 rows only.']] : []),
    columns.map((column) => column.label),
    ...rows.map((row) => columns.map((column) => row[column.field])),
  ];
  return '﻿' + lines.map((line) => line.map(cell).join(',')).join('\r\n');
}
